"use client";
import React from "react";
import { AbsoluteFill, Sequence, useCurrentFrame, useVideoConfig, interpolate } from "remotion";
import { loadFont } from "@remotion/google-fonts/NotoSansDevanagari";
import { HOLES_DATA_URI } from "./trimbakHolesData";
import { DevaKathaLS1 } from "./DevaKathaLS1";
import { DevaKathaLS7 } from "./DevaKathaLS7";

const { fontFamily } = loadFont("normal", { weights: ["400", "700", "800", "900"], subsets: ["devanagari", "latin"] });

// Amarnath phrase overlays — Trimbakeshwar holes treatment. Top-left, yellow-prominent,
// per-word colour + line breaks, black drop-shadow, scattered-holes mask, word-by-word drop-in.

const PAD = 25;
const YELLOW = "#FFD60A";
const WHITE = "#FFFFFF";
const RED = "#FF3B2E";
const SH = "0 5px 14px rgba(0,0,0,0.9), 0 2px 5px rgba(0,0,0,0.95)";
const HOLES = HOLES_DATA_URI;

const BASE_FS = 66, BIG_FS = 92, SMALL_FS = 46;
const STAGGER = 0.16, DROP = 26, DUR = 0.32;
const LH = 1.18;
const LINE_GAP = -10;

type Word = { t: string; c?: "y" | "w" | "r"; big?: boolean; small?: boolean };
export type Phrase = { lines: Word[][]; start: number; out: number; lineGap?: number; lh?: number; ls?: string };
const col = (c?: string) => (c === "w" ? WHITE : c === "r" ? RED : YELLOW);

// 37 approved cards (Yash review 2026-06-24). start = exact word_timestamps_hindi anchor.
export const AMAR_PHRASES: Phrase[] = [
  { start: 2.54,   out: 6.0,    lines: [[{ t: "20", c: "w", big: true }, { t: "फीट", c: "y" }, { t: "ऊँचा", c: "y" }]] },
  { start: 8.80,   out: 12.2,   lines: [[{ t: "ग्लोबल", c: "y" }, { t: "वार्मिंग", c: "r", big: true }]] },
  { start: 42.60,  out: 46.0,   lines: [[{ t: "मुंडमाला", c: "y" }], [{ t: "का", small: true }, { t: "राज़", c: "w", big: true }]] },
  { start: 64.52,  out: 67.9,   lines: [[{ t: "अमर", c: "y", big: true }, { t: "होने", small: true }], [{ t: "का", small: true }, { t: "रहस्य", c: "w" }]] },
  { start: 74.65,  out: 76.55,  lines: [[{ t: "परम", c: "y" }, { t: "रहस्य", c: "w", big: true }]] },
  { start: 76.79,  out: 80.2,   lines: [[{ t: "मृत्यु", c: "r" }, { t: "से", small: true }, { t: "मुक्ति", c: "y", big: true }]] },
  { start: 132.97, out: 136.4,  lines: [[{ t: "पहलगाम", c: "y", big: true }], [{ t: "नंदी", c: "w" }, { t: "छूटे", small: true }]] },
  { start: 139.17, out: 142.6,  lines: [[{ t: "चंदनवाड़ी", c: "y", big: true }], [{ t: "चंद्रमा", c: "w" }, { t: "का", small: true }, { t: "त्याग", small: true }]] },
  { start: 148.42, out: 151.8,  lines: [[{ t: "शेषनाग", c: "y", big: true }], [{ t: "सर्प", c: "w" }, { t: "त्यागे", small: true }]] },
  { start: 161.03, out: 164.4,  lines: [[{ t: "महागुनस", c: "y", big: true }], [{ t: "गणेश", c: "w" }, { t: "छूटे", small: true }]] },
  { start: 169.92, out: 173.3,  lines: [[{ t: "पंचतरणी", c: "y", big: true }]] },
  { start: 193.06, out: 196.5,  lines: [[{ t: "पाँच", c: "w", big: true }, { t: "तत्व", c: "y" }], [{ t: "त्यागे", c: "y" }]] },
  { start: 212.15, out: 215.9,  lines: [[{ t: "चट्टानें", c: "w" }], [{ t: "बंजर", c: "r", big: true }]] },
  { start: 216.06, out: 219.5,  lines: [[{ t: "नदियाँ", c: "w" }], [{ t: "जम", c: "y", big: true }, { t: "गईं", small: true }]] },
  { start: 265.79, out: 269.2,  lines: [[{ t: "3,880", c: "w", big: true }], [{ t: "मीटर", c: "y" }, { t: "ऊँचाई", small: true }]] },
  { start: 273.41, out: 276.8,  lines: [[{ t: "पवित्र", c: "y" }], [{ t: "अमरनाथ", c: "w", big: true }, { t: "गुफा", c: "y" }]] },
  { start: 290.13, out: 293.5,  lines: [[{ t: "कालाग्नि", c: "r", big: true }], [{ t: "रुद्र", c: "y" }]] },
  { start: 379.53, out: 382.9,  lines: [[{ t: "दो", c: "w", big: true }, { t: "कबूतर", c: "y" }]] },
  { start: 424.54, out: 427.9,  lines: [[{ t: "कबूतरों", c: "y" }], [{ t: "को", small: true }, { t: "श्राप", c: "r", big: true }]] },
  { start: 463.35, out: 466.8,  lines: [[{ t: "रसलिङ्ग", c: "y", big: true }], [{ t: "स्वयं", small: true }, { t: "अमरेश्वर", c: "w" }]] },
  { start: 525.27, out: 528.7,  lines: [[{ t: "अमरावती", c: "y" }], [{ t: "में", small: true }, { t: "स्नान", c: "w", big: true }]] },
  { start: 558.29, out: 561.7,  lines: [[{ t: "मंत्र", small: true }], [{ t: "शितिकंठेश", c: "y", big: true }]] },
  { start: 633.95, out: 637.3,  lines: [[{ t: "राजतरंगिणी", c: "y" }], [{ t: "900", c: "w", big: true }, { t: "साल", small: true }]] },
  { start: 680.17, out: 683.6,  lines: [[{ t: "बर्फ", c: "w" }, { t: "का", small: true }], [{ t: "लिंगम", c: "y", big: true }]] },
  { start: 729.50, out: 732.9,  lines: [[{ t: "पिस्सू", c: "y", big: true }, { t: "टॉप", c: "w" }]] },
  { start: 784.47, out: 787.9,  lines: [[{ t: "अगस्त", small: true }, { t: "2000", c: "w", big: true }], [{ t: "Terrorist", c: "r" }, { t: "Attack", c: "r" }]] },
  { start: 826.82, out: 830.2,  lines: [[{ t: "32", c: "w", big: true }, { t: "बेगुनाह", c: "r" }], [{ t: "मारे", small: true }, { t: "गए", small: true }]] },
  { start: 843.54, out: 846.9,  lines: [[{ t: "2001", c: "w", big: true }], [{ t: "शेषनाग", c: "y" }, { t: "पर", small: true }, { t: "Attack", c: "r" }]] },
  { start: 869.32, out: 872.7,  lines: [[{ t: "2017", c: "w", big: true }], [{ t: "बस", small: true }, { t: "यात्रियों", c: "y" }, { t: "पर", small: true }, { t: "हमला", c: "r" }]] },
  { start: 1005.70, out: 1009.1, lines: [[{ t: "5,12,000", c: "w", big: true }], [{ t: "श्रद्धालु", c: "y" }]] },
  { start: 1038.00, out: 1041.4, lines: [[{ t: "रोज़", small: true }, { t: "12", c: "w", big: true }, { t: "टन", c: "w" }], [{ t: "कचरा", c: "r" }]] },
  { start: 1068.07, out: 1071.5, lines: [[{ t: "1996", c: "w", big: true }], [{ t: "243", c: "r", big: true }, { t: "मौतें", c: "y" }]] },
  { start: 1112.02, out: 1115.4, lines: [[{ t: "RFID", c: "y", big: true }], [{ t: "टैग", c: "w" }, { t: "ज़रूरी", small: true }]] },
  { start: 1159.03, out: 1162.4, lines: [[{ t: "छड़ी", c: "y" }], [{ t: "मुबारक", c: "w", big: true }]] },
  { start: 1256.67, out: 1260.1, lines: [[{ t: "अब", small: true }, { t: "डेढ़–दो", c: "y", big: true }], [{ t: "फीट", c: "w" }]] },
  { start: 1280.14, out: 1283.5, lines: [[{ t: "मोक्ष", c: "y" }, { t: "या", small: true }], [{ t: "विनाश?", c: "r", big: true }]] },
  { start: 1322.24, out: 1325.6, lines: [[{ t: "यही", small: true }, { t: "असली", c: "y" }], [{ t: "अमरत्व", c: "w", big: true }]] },
];

const PhraseBlock: React.FC<{ p: Phrase; fps: number }> = ({ p, fps }) => {
  const frame = useCurrentFrame();
  const t = frame / fps;
  const phraseOp = interpolate(t, [p.out - 0.4, p.out], [1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  if (t < p.start - 0.1 || t > p.out + 0.1) return null;
  const gap = p.lineGap ?? LINE_GAP;
  const lh = p.lh ?? LH;
  return (
    <div style={{ position: "absolute", top: PAD, left: PAD, fontFamily, opacity: phraseOp, display: "flex", flexDirection: "column" }}>
      {p.lines.map((line, li) => (
        <div key={li} style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 16, marginTop: li === 0 ? 0 : gap }}>
          {line.map((w, gi) => {
            const aT = p.start + (li * 2 + gi) * STAGGER;
            const k = Math.min(Math.max((t - aT) / DUR, 0), 1);
            const ease = 1 - Math.pow(1 - k, 3);
            const op = interpolate(t, [aT, aT + 0.18], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
            const y = -DROP * (1 - ease);
            return (
              <span key={gi} style={{
                color: col(w.c),
                fontSize: w.big ? BIG_FS : w.small ? SMALL_FS : BASE_FS,
                fontWeight: w.big ? 900 : 800,
                lineHeight: lh,
                letterSpacing: p.ls ?? "0",
                textShadow: SH,
                opacity: op,
                transform: `translateY(${y}px)`,
                display: "inline-block",
              }}>{w.t}</span>
            );
          })}
        </div>
      ))}
    </div>
  );
};

// Bottom-left viewing tip — half the size of the date, white, NO holes mask.
const WatchTag: React.FC<{ fps: number; start: number; out: number }> = ({ fps, start, out }) => {
  const t = useCurrentFrame() / fps;
  if (t < start - 0.1 || t > out + 0.1) return null;
  const op = interpolate(t, [start, start + 0.4, out - 0.4, out], [0, 1, 1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <div style={{ position: "absolute", bottom: PAD + 6, left: PAD, fontFamily, opacity: op }}>
      <span style={{ color: WHITE, fontSize: SMALL_FS, fontWeight: 700, letterSpacing: "0.01em", textShadow: SH }}>
        Watch at 1.2x for best experience
      </span>
    </div>
  );
};

// Master comp — holes-masked devotional phrases + non-masked UI (watch tip, LS pills).
export const AmarnathOverlaysFull: React.FC<{ phrases?: Phrase[] }> = ({ phrases = AMAR_PHRASES }) => {
  const { fps } = useVideoConfig();
  const S = (s: number) => Math.round(s * fps);
  return (
    <AbsoluteFill>
      {/* holes distress mask over the dramatic top-left phrases only */}
      <AbsoluteFill style={{
        backgroundColor: "transparent",
        WebkitMaskImage: `url(${HOLES})`, maskImage: `url(${HOLES})`,
        WebkitMaskSize: "1280px 704px", maskSize: "1280px 704px",
        WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
        WebkitMaskPosition: "0 0", maskPosition: "0 0",
      }}>
        {phrases.map((p, i) => <PhraseBlock key={i} p={p} fps={fps} />)}
      </AbsoluteFill>

      {/* viewing tip (bottom-left, half-size), shows over the opening */}
      <WatchTag fps={fps} start={1.0} out={6.5} />

      {/* Like+Subscribe — LS1 @ early "सब्सक्राइब" ~108s · LS7 @ outro "सब्सक्राइब" ~1369s */}
      <Sequence from={S(108)} durationInFrames={S(5.2)}><DevaKathaLS1 /></Sequence>
      <Sequence from={S(1369)} durationInFrames={S(5.2)}><DevaKathaLS7 /></Sequence>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
